import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import ImageWrapper from "@/components/ImageComponent/ImageWrapper";

const PujaStyleAccordion = () => {
    const [activeAccordion, setActiveAccordion] = useState(null);

    const toggleAccordion = (style) => {
        setActiveAccordion(activeAccordion === style ? null : style);
    };

    const handleBookNow = (panditType) => {
        document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' });
    };

    const southIndianOptions = [
        { name: 'Kannada Purohits', description: 'Traditional Karnataka style rituals' },
        { name: 'Telugu Poojari', description: 'Authentic Andhra Pradesh traditions' },
        { name: 'Tamil Vadhyars', description: 'Classical Tamil Nadu ceremonies' },
        { name: 'Malayalam Pandit', description: 'Kerala temple style worship' }
    ];

    const northIndianOptions = [
        { name: 'Hindi North Indian Pandits', description: 'Traditional Vedic rituals' },
        { name: 'Marathi Gurujis', description: 'Maharashtra cultural traditions' },
        { name: 'Gujarati Pandits', description: 'Gujarat regional ceremonies' },
        { name: 'Marwari Pandits', description: 'Rajasthani traditional worship' },
        { name: 'Bengali Pandits', description: 'Bengal cultural rituals' }
    ];

    return (
        <div className="flex-1 w-full max-w-xl lg:max-w-lg xl:max-w-xl">
            <div className="space-y-4 lg:space-y-6">

                {/* South Indian Style */}
                <div className="relative">
                    <div
                        className="relative group cursor-pointer w-full h-48 sm:h-52 lg:h-56 xl:h-64"
                        onClick={() => toggleAccordion('south')}
                    >
                        <ImageWrapper
                            src="/images/hero_banner_2.jpg"
                            alt="South Indian Style Puja"
                            className="rounded-xl shadow-lg object-cover"
                            priority={true}
                            loading="eager"
                            fetchPriority="high"
                            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 576px"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent rounded-xl">
                            <div className="absolute bottom-4 left-4 text-white">
                                <h3 className="font-semibold text-lg flex items-center gap-2">
                                    South Indian Style
                                    {activeAccordion === 'south' ?
                                        <ChevronUp className="w-5 h-5" /> :
                                        <ChevronDown className="w-5 h-5" />
                                    }
                                </h3>
                                <p className="text-sm opacity-90">Classical Temple Traditions</p>
                            </div>
                        </div>
                    </div>

                    {/* South Indian Accordion Content */}
                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        activeAccordion === 'south'
                            ? 'max-h-[500px] opacity-100'
                            : 'max-h-0 opacity-0'
                    }`}>
                        <div className="bg-white rounded-b-xl shadow-lg border-t-0 p-4 space-y-3">
                            {southIndianOptions.map((option, index) => (
                                <div key={index} className="p-3 bg-orange-50 rounded-lg border border-orange-100 hover:bg-orange-100 transition-colors">
                                    <h4 className="font-medium text-gray-800 text-sm">{option.name}</h4>
                                    <p className="text-xs text-gray-600 mt-1">{option.description}</p>
                                </div>
                            ))}

                            {/* Single Book Now Button for South Indian */}
                            <div className="pt-2">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleBookNow('South Indian Style');
                                    }}
                                    className="w-full bg-BluntRed hover:bg-[#6B1419] text-white px-6 py-3 rounded-md text-sm font-medium transition-colors duration-200 shadow-sm hover:shadow-md"
                                >
                                    Book Now - South Indian Style
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* North Indian Style */}
                <div className="relative">
                    <div
                        className="relative group cursor-pointer w-full h-48 sm:h-52 lg:h-56 xl:h-64"
                        onClick={() => toggleAccordion('north')}
                    >
                        <ImageWrapper
                            src="/images/hero_banner.jpg"
                            alt="North Indian Style Puja"
                            className="rounded-xl shadow-lg object-cover transition-transform duration-300 group-hover:scale-105"
                            loading="lazy"
                            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 576px"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent rounded-xl">
                            <div className="absolute bottom-4 left-4 text-white">
                                <h3 className="font-semibold text-lg flex items-center gap-2">
                                    North Indian Style
                                    {activeAccordion === 'north' ?
                                        <ChevronUp className="w-5 h-5" /> :
                                        <ChevronDown className="w-5 h-5" />
                                    }
                                </h3>
                                <p className="text-sm opacity-90">Traditional Vedic Rituals</p>
                            </div>
                        </div>
                    </div>

                    {/* North Indian Accordion Content */}
                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        activeAccordion === 'north'
                            ? 'max-h-[500px] opacity-100'
                            : 'max-h-0 opacity-0'
                    }`}>
                        <div className="bg-white rounded-b-xl shadow-lg border-t-0 p-4 space-y-3">
                            {northIndianOptions.map((option, index) => (
                                <div key={index} className="p-3 bg-orange-50 rounded-lg border border-orange-100 hover:bg-orange-100 transition-colors">
                                    <h4 className="font-medium text-gray-800 text-sm">{option.name}</h4>
                                    <p className="text-xs text-gray-600 mt-1">{option.description}</p>
                                </div>
                            ))}

                            {/* Single Book Now Button for North Indian */}
                            <div className="pt-2">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleBookNow('North Indian Style');
                                    }}
                                    className="w-full bg-BluntRed hover:bg-[#6B1419] text-white px-6 py-3 rounded-md text-sm font-medium transition-colors duration-200 shadow-sm hover:shadow-md"
                                >
                                    Book Now - North Indian Style
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default PujaStyleAccordion;